import { useAtsDeactivation, useIsAdmin, useIsJobillan, useWhatsAppConversations } from '@/hooks'
import { useAuth } from '@/lib'
import { Glyphs, LinkedJobibot } from '@/ui'
import { ProvidedMainNavConfigContext } from '@/ui'
import { LanguageSelector } from '@/ui'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import ctl from '@netlify/classnames-template-literals'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { NavWidgetBoundary } from './nav-widget-boundary'
import { NotificationBell } from './notification-bell'

// Simple user button replacement for WorkOS
function UserButton() {
	const { user, signOut } = useAuth()

	if (!user) { return null }

	const initials = user.firstName && user.lastName
		? `${user.firstName[0]}${user.lastName[0]}`
		: user.email?.[0]?.toUpperCase() ?? '?'

	return (
		<div className="relative group">
			<button
				className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center text-sm font-medium"
				title={user.email ?? 'User'}
			>
				{initials}
			</button>
			<div className="absolute right-0 top-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 py-1 hidden group-hover:block min-w-[120px]">
				<button
					onClick={() => signOut()}
					className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
				>
					Sign out
				</button>
			</div>
		</div>
	)
}

export type PortalMainNavProps = {
	activeMainNavLink?: 'campaigns' | 'talentPool' | 'analytics' | 'whatsapp' | 'settings'
}

export function PortalMainNav({ activeMainNavLink }: PortalMainNavProps) {
	const { t } = useTranslation('common')
	const { t: tAnalytics } = useTranslation('analytics')
	const { can } = useAuth()
	const isAdmin = useIsAdmin()
	const isJobillan = useIsJobillan()
	const canSeeAnalytics = isAdmin || isJobillan
	const isWhatsAppEnabled = useWhatsAppEnabled()
	const { unreadConversationsCount } = useWhatsAppConversations({}, { enabled: isWhatsAppEnabled })
	const atsDeactivation = useAtsDeactivation()
	const {
		logoLinker,
		campaignsLinker,
		talentPoolLinker,
		analyticsLinker,
		whatsAppLinker,
		newCampaignLinker,
		preferencesLinker,
		profileLinker,
		orgSwitcher,
	} = useContext(
		ProvidedMainNavConfigContext,
	)

	const leftSideMenuItemClassName = (active: boolean) =>
		ctl(`
		flex items-center gap-2 px-2 xl:px-4 py-4
		text-md
		whitespace-nowrap
		relative
		${active ? 'text-brand-600 font-semibold' : 'text-grey-700 font-medium'}
	`)

	const leftSideMenuHighlightClassName = ctl(`
		absolute inset-x-[8px] xl:inset-x-[16px] bottom-0 h-[1px] bg-brand-600 rounded-full
	`)

	return (
		<div
			className={ctl(`flex items-center mx-auto justify-between max-w-[var(--max-width)]`)}
		>
			<div className="flex items-center">
				<div className="mr-4 relative top-[-px]">
					<LinkedJobibot size={28} />
				</div>
				{campaignsLinker({
					children: (
						<span
							className={leftSideMenuItemClassName(activeMainNavLink === 'campaigns')}
						>
							<span>{t('nav.campaigns')}</span>
							{activeMainNavLink === 'campaigns' && <span className={leftSideMenuHighlightClassName} />}
						</span>
					),
				})}
				{talentPoolLinker({
					children: (
						<span
							className={leftSideMenuItemClassName(activeMainNavLink === 'talentPool')}
						>
							<span>{t('nav.talent_pool')}</span>
							{activeMainNavLink === 'talentPool' && <span className={leftSideMenuHighlightClassName} />}
						</span>
					),
				})}
				{isWhatsAppEnabled && whatsAppLinker({
					children: (
						<span
							className={leftSideMenuItemClassName(activeMainNavLink === 'whatsapp')}
						>
							<span>{t('nav.whatsapp')}</span>
							{unreadConversationsCount > 0 && (
								<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-semibold text-white bg-purple-500 rounded-full">
									{unreadConversationsCount > 9 ? '9+' : unreadConversationsCount}
								</span>
							)}
							{activeMainNavLink === 'whatsapp' && <span className={leftSideMenuHighlightClassName} />}
						</span>
					),
				})}
				{canSeeAnalytics && analyticsLinker({
					children: (
						<span
							className={leftSideMenuItemClassName(activeMainNavLink === 'analytics')}
						>
							<span>{tAnalytics('organisationAnalytics.title')}</span>
							{activeMainNavLink === 'analytics' && <span className={leftSideMenuHighlightClassName} />}
						</span>
					),
				})}
				{preferencesLinker({
					children: (
						<span
							className={leftSideMenuItemClassName(activeMainNavLink === 'settings')}
						>
							<span>{t('nav.settings')}</span>
							{atsDeactivation && (
								<span
									aria-label={t('nav.settingsAttention')}
									title={t('nav.settingsAttention')}
									className="size-2 rounded-full bg-amber-500"
								/>
							)}
							{activeMainNavLink === 'settings' && <span className={leftSideMenuHighlightClassName} />}
						</span>
					),
				})}
			</div>
			<div className="flex items-center gap-2 xl:gap-4">
				{can('campaign:create') && newCampaignLinker({
					children: (
						<span
							className={ctl(`
									flex gap-2 items-center whitespace-nowrap
									px-3 py-2 bg-gradient-to-br from-indigo-700 via-indigo-600 via-40% to-brand-600
									z-10 shadow-brand-500/30
									text-white rounded-md
									transition-colors
								`)}
						>
							<div className="size-[14px]">
								<Glyphs.core.PlusIcon />
							</div>
							{t('nav.new_campaign')}
						</span>
					),
				})}

				<LanguageSelector />

				<div className="ring ring-grey-200 rounded-md p-1.25 flex items-center">
					{orgSwitcher}
				</div>

				<NavWidgetBoundary widget="notification-bell">
					<NotificationBell />
				</NavWidgetBoundary>

				{profileLinker({
					children: (
						<span
							className={ctl(`flex items-center gap-2 px-3 py-5`)}
						>
							<UserButton />
						</span>
					),
				})}
			</div>
		</div>
	)
}
